import usePageSEO from '../hooks/usePageSEO';
import { WebPageSchema } from '../components/JsonLd';

import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import Footer from '../components/Footer';

const LegalPage = () => {
  usePageSEO({
    title: 'Privacy Policy & Terms of Service | Plouton AI',
    description: 'Read Plouton AI privacy policy, terms of service, and data security practices. GDPR compliant. SOC 2 Type II certified.',
    path: '/legal'
  });

  const { hash } = useLocation();

  useEffect(() => {
    if (hash) {
      const el = document.getElementById(hash.replace('#', ''));
      if (el) {
        setTimeout(() => el.scrollIntoView({ behavior: 'smooth', block: 'start' }), 100);
      }
    }
  }, [hash]);

  return (
    <div className="min-h-screen bg-white pt-[108px]">
      <WebPageSchema
        name="Legal | Plouton AI"
        description="Privacy policy, terms of service, and data security practices."
        url="https://plouton.co/legal"
      />
      <article className="py-12 sm:py-16">
        <div className="max-w-3xl mx-auto px-6 sm:px-8 lg:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h1 className="font-heading text-3xl sm:text-4xl font-bold text-slate-900 mb-4">
              Legal
            </h1>
            <p className="text-slate-500 mb-16">
              Last updated: April 1, 2026
            </p>

            {/* Privacy */}
            <section id="privacy" className="scroll-mt-32 mb-20">
              <h2 className="font-heading text-2xl font-bold text-slate-900 mb-6">Privacy Policy</h2>
              <div className="space-y-4 text-slate-600 leading-relaxed">
                <p>
                  At Plouton Technology Labs, PTE. LTD. ("Plouton AI," "we," "us"), we take data privacy seriously. This Privacy Policy describes how we collect, use, and protect your information when you use our services.
                </p>

                <h3 className="font-heading text-lg font-semibold text-slate-900 pt-4">Information We Collect</h3>
                <p>We collect information you provide directly, including:</p>
                <ul className="space-y-2 ml-6">
                  <li className="list-disc text-slate-500">Account details (name, email, company)</li>
                  <li className="list-disc text-slate-500">Usage data and workflow configurations</li>
                  <li className="list-disc text-slate-500">Support communications</li>
                  <li className="list-disc text-slate-500">Analytics and log data</li>
                </ul>

                <h3 className="font-heading text-lg font-semibold text-slate-900 pt-4">How We Use Your Information</h3>
                <p>We use collected data to:</p>
                <ul className="space-y-2 ml-6">
                  <li className="list-disc text-slate-500">Provide and improve our automation services</li>
                  <li className="list-disc text-slate-500">Authenticate users and maintain account security</li>
                  <li className="list-disc text-slate-500">Send product updates and relevant communications</li>
                  <li className="list-disc text-slate-500">Comply with legal obligations</li>
                </ul>

                <h3 className="font-heading text-lg font-semibold text-slate-900 pt-4">Data Protection</h3>
                <p>
                  All data is encrypted in transit (TLS 1.3) and at rest (AES-256). We maintain SOC 2 Type II certification and are GDPR compliant. We never sell your personal data to third parties.
                </p>

                <h3 className="font-heading text-lg font-semibold text-slate-900 pt-4">Your Rights</h3>
                <p>
                  You have the right to access, correct, delete, or export your personal data at any time. To exercise these rights, contact us at <a href="mailto:privacy@plouton.co" className="text-blue-600 hover:underline">privacy@plouton.co</a>.
                </p>
              </div>
            </section>

            {/* Terms */}
            <section id="terms" className="scroll-mt-32 mb-20">
              <h2 className="font-heading text-2xl font-bold text-slate-900 mb-6">Terms of Service</h2>
              <div className="space-y-4 text-slate-600 leading-relaxed">
                <p>
                  These Terms of Service ("Terms") govern your access to and use of Plouton AI's products and services. By using our services, you agree to be bound by these Terms.
                </p>

                <h3 className="font-heading text-lg font-semibold text-slate-900 pt-4">Service Description</h3>
                <p>
                  Plouton AI provides browser-based AI agent services for finance workflow automation. Our agents operate through web interfaces on behalf of authorized users to perform tasks such as invoice processing, bank reconciliation, and financial close operations.
                </p>

                <h3 className="font-heading text-lg font-semibold text-slate-900 pt-4">User Responsibilities</h3>
                <ul className="space-y-2 ml-6">
                  <li className="list-disc text-slate-500">Maintain the confidentiality of your account credentials</li>
                  <li className="list-disc text-slate-500">Ensure you have authorization to automate workflows in connected systems</li>
                  <li className="list-disc text-slate-500">Comply with all applicable laws and regulations</li>
                  <li className="list-disc text-slate-500">Not use the service for any unlawful or prohibited purpose</li>
                </ul>

                <h3 className="font-heading text-lg font-semibold text-slate-900 pt-4">Intellectual Property</h3>
                <p>
                  Plouton AI retains all rights to its technology, software, and proprietary processes. Your data remains yours — we claim no ownership of the information processed through our platform.
                </p>

                <h3 className="font-heading text-lg font-semibold text-slate-900 pt-4">Limitation of Liability</h3>
                <p>
                  Plouton AI provides services "as is" without warranties of any kind. We are not liable for indirect, incidental, or consequential damages arising from the use of our services. Our total liability shall not exceed the fees paid by you in the twelve months preceding the claim.
                </p>

                <h3 className="font-heading text-lg font-semibold text-slate-900 pt-4">Termination</h3>
                <p>
                  Either party may terminate the agreement with 30 days' written notice. Upon termination, we will provide a data export within 14 business days and securely delete all stored data within 90 days.
                </p>
              </div>
            </section>

            {/* Security */}
            <section id="security" className="scroll-mt-32">
              <h2 className="font-heading text-2xl font-bold text-slate-900 mb-6">Security</h2>
              <div className="space-y-4 text-slate-600 leading-relaxed">
                <p>
                  Security is foundational to Plouton AI. We implement enterprise-grade protections across our infrastructure to ensure the safety and integrity of your financial data.
                </p>

                <h3 className="font-heading text-lg font-semibold text-slate-900 pt-4">Compliance & Certifications</h3>
                <ul className="space-y-2 ml-6">
                  <li className="list-disc text-slate-500"><strong className="text-slate-700">SOC 2 Type II</strong> — Independently audited controls for security, availability, and confidentiality</li>
                  <li className="list-disc text-slate-500"><strong className="text-slate-700">GDPR Compliant</strong> — Full compliance with EU data protection regulations</li>
                  <li className="list-disc text-slate-500"><strong className="text-slate-700">99.99% Uptime SLA</strong> — Guaranteed service availability with proactive monitoring</li>
                </ul>

                <h3 className="font-heading text-lg font-semibold text-slate-900 pt-4">Infrastructure Security</h3>
                <ul className="space-y-2 ml-6">
                  <li className="list-disc text-slate-500">All data encrypted with AES-256 at rest and TLS 1.3 in transit</li>
                  <li className="list-disc text-slate-500">Isolated, sandboxed execution environments for every agent session</li>
                  <li className="list-disc text-slate-500">No credentials stored in plain text — vault-based secret management</li>
                  <li className="list-disc text-slate-500">Regular penetration testing and vulnerability assessments</li>
                </ul>

                <h3 className="font-heading text-lg font-semibold text-slate-900 pt-4">Access Controls</h3>
                <ul className="space-y-2 ml-6">
                  <li className="list-disc text-slate-500">Role-based access control (RBAC) with principle of least privilege</li>
                  <li className="list-disc text-slate-500">Multi-factor authentication for all administrative access</li>
                  <li className="list-disc text-slate-500">Complete audit trail with screenshot evidence for every agent action</li>
                  <li className="list-disc text-slate-500">Real-time anomaly detection and alerting</li>
                </ul>

                <h3 className="font-heading text-lg font-semibold text-slate-900 pt-4">Reporting Vulnerabilities</h3>
                <p>
                  If you discover a security vulnerability, please report it to <a href="mailto:security@plouton.co" className="text-blue-600 hover:underline">security@plouton.co</a>. We investigate all reports and respond within 24 hours.
                </p>
              </div>
            </section>
          </motion.div>
        </div>
      </article>

      <Footer />
    </div>
  );
};

export default LegalPage;
